import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MdViewList, MdPercent, MdSavings, MdAddCircleOutline, MdPublic, MdOpenInNew } from "react-icons/md";
import { partnerPageUrls } from "lib/partnerPages";
import Card from "components/card";
import Widget from "components/widget/Widget";
import StatusBadge from "components/admin/StatusBadge";
import { apiGet, buildQueryString } from "lib/apiClient";
import { formatKes } from "lib/format";
import { useAuth } from "contexts/AuthContext";
import { MoneyFlowLog, PartnerEarningsTiles, PeriodPicker, bpsLabel, useOwnPartnerRevenue } from "components/revenue/RevenueStatement";

export default function PartnerOverviewPage() {
  const { portal } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [marketCount, setMarketCount] = useState(null);
  const [marketError, setMarketError] = useState(null);
  const { data: revenue, error: revenueError } = useOwnPartnerRevenue(period);

  useEffect(() => {
    const load = () =>
      apiGet(`/portal/markets${buildQueryString({ page: 1, pageSize: 1 })}`)
        .then((result) => {
          setMarketCount(result.total ?? 0);
          setMarketError(null);
        })
        .catch((err) => setMarketError(err.message || "Failed to load your markets."));
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const partner = portal?.partner;
  const pageUrls = partnerPageUrls(partner?.slug);
  const tenantName = portal?.tenant?.name || "your tenant";
  const tenantPct = bpsLabel(portal?.tenantShareBps ?? 8000);

  return (
    <div>
      {partner && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-2xl font-semibold text-navy-700 dark:text-white">{partner.name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">A partner of {tenantName}</p>
          </div>
          <StatusBadge
            label={partner.status === "ACTIVE" ? "Active" : partner.status === "CLOSED" ? "Closed" : "Paused"}
            tone={partner.status === "ACTIVE" ? "green" : partner.status === "CLOSED" ? "gray" : "amber"}
          />
        </div>
      )}

      {pageUrls && (
        <Card extra="mb-5 p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <MdPublic className="mt-0.5 h-6 w-6 shrink-0 text-brand-500" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy-700 dark:text-white">Your site</p>
                <div className="mt-1 flex flex-col gap-0.5 text-sm">
                  <a href={pageUrls.subdomain} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all font-medium text-brand-500 hover:underline">
                    {pageUrls.subdomain} <MdOpenInNew className="h-3.5 w-3.5 shrink-0" />
                  </a>
                </div>
                <p className="mt-1 text-xs text-gray-500">Your clients trade here. Trades placed on your site are credited to you.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/portal/my-site" className="text-sm font-semibold text-brand-500">
                Customise your site →
              </Link>
              <Link to="/portal/performance" className="text-sm font-semibold text-brand-500">
                Performance →
              </Link>
              <Link to="/portal/curated-markets" className="text-sm font-semibold text-brand-500">
                Featured markets →
              </Link>
            </div>
          </div>
        </Card>
      )}

      {(marketError || revenueError) && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{marketError || revenueError}</div>}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <Widget icon={<MdViewList className="h-7 w-7" />} title="Your markets" subtitle={marketCount === null ? "…" : marketCount.toLocaleString()} />
        <Widget icon={<MdPercent className="h-7 w-7" />} title={`Your share of ${tenantName}'s ${tenantPct}`} subtitle={partner ? bpsLabel(partner.shareBps) : "…"} />
        <Widget
          icon={<MdSavings className="h-7 w-7" />}
          title="Your share this period"
          subtitle={revenue ? formatKes(revenue.partner.split.partner, revenue.currency) : "…"}
        />
      </div>

      <Card extra="mt-5 p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-navy-700 dark:text-white">Revenue from your markets</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Net revenue splits {tenantPct} to {tenantName} and the rest to the platform. You get {partner ? bpsLabel(partner.shareBps) : "your share"} of {tenantName}'s {tenantPct}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodPicker value={period} onChange={setPeriod} />
            <Link to="/portal/my-revenue" className="text-sm font-semibold text-brand-500">
              My revenue →
            </Link>
          </div>
        </div>
        <PartnerEarningsTiles revenue={revenue} portal={portal} />
      </Card>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <MoneyFlowLog period={period} variant="partner" pageSize={8} title="Recent revenue" />
        </div>
        <Card extra="p-5">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Create a market</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Every market you create earns you your share of the revenue it brings in. {tenantName} reviews and approves it before it opens.
          </p>
          <Link to="/portal/markets/new" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500">
            <MdAddCircleOutline className="h-4 w-4" /> New market
          </Link>
          <Link to="/portal/guide" className="mt-2 inline-block text-sm font-semibold text-brand-500">
            Read the market guide →
          </Link>
        </Card>
      </div>
    </div>
  );
}
